// 警報工作台（EVT-07，SPEC #vms-web-alarm-workbench，app/vms#18）：「事件與警報 › 警報」分頁 ≥ 1280 左右分割（左緊湊清單、右詳細）、
// 768–1279 清單＋右側抽屜；處理不換頁。鍵盤與按鈕走同一個函式（同一個 expected_version 路徑、同一個整波套用）；
// R / F 後自動到原本排在下面、還沒處理的那則；409 時右欄重畫、選取與焦點不動。指派與接手時限的資料來自平台（EVT-07 API）。
// 排序、合併、已恢復、未登記都沿用事件頁算好的列（警報整理），這裡只負責版型、選取與處理。
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { lensOf } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import { useScope } from '../../lib/scope';
import { aggKey, useAlarmStates } from '../../lib/alarmStates';
import { ALARM_NOTE_MAX, alarmApi, conflictOf, isUnhandled, noteOK, type AlarmChange, type AlarmInfo, type AlarmSla, type AlarmStateName } from '../../lib/alarms';
import {
  KEY_STATE,
  STATE_KEY,
  WB_FILTERS,
  isMine,
  isOverdue,
  keepSelection,
  nextUnhandled,
  passFilter,
  slaChipText,
  slaMetAt,
  slaMetText,
  slaRunning,
  slaView,
  wbKeyOf,
  workbenchActions,
  type WbFilter,
  type WbKey,
  type WbRowFacts,
} from '../../lib/alarmWorkbench';
import { handling, recoveredAt } from '../../lib/alarmTidy';
import { useNow } from '../../lib/useNow';
import { stamp } from '../../lib/timefmt';
import { formatDateTime } from '../../lib/tz';
import { linkApi, type SearchDoc } from '../../lib/planeApi';
import { fullName, type VmsDevice } from '../../lib/devices';
import { playbackHref } from '../../lib/links';
import type { Episode } from '../../lib/eventEpisodes';
import { AlarmStatus } from '../../components/AlarmActions';
import { AlarmHistoryList } from '../../components/AlarmHistory';
import { EventThumb } from '../../components/EventThumb';
import { Dialog } from '../../components/Dialog';
import { LiveTile } from '../live/LiveTile';
import { deviceHref } from '../devices/DeviceDetailPage';
import { useFloorplanImage, useFloorplanOwners, useFloorplans } from '../map/useMapData';
import { ClipPlayer } from './EventDetailPage';
import { EpisodeNote } from './EventsPage';

/** 工作台的一列（事件頁算好的：排序、合併、次數、已開多久都照警報整理）。 */
export interface WbItem {
  /** `${home_id}/${event_id}`（網址 sel、listbox option） */
  id: string;
  doc: SearchDoc;
  device?: VmsDevice;
  homeName: string;
  ts: number;
  ep?: Episode;
  /** 事件顯示名、設備完整名 */
  name: string;
  devLabel: string;
  /** 「× N 次（最早 HH:MM）」；1 次 = 空 */
  times: string;
  n: number;
  /** 合併列整波（不含自己）的 event_id：處理列的動作一起套用 */
  also: string[];
  /** 最早那則的時間 ms（已開多久） */
  since: number;
  /** 「已開 N 分鐘」；已結案 = 空 */
  age: string;
  unseen: boolean;
  listed: boolean;
  /** 這一列是一次的結束、清單裡沒有開始那筆（開始時還不是警報：布防前就開始的偵測；或沒收到開始紀錄） */
  startedBeforeAlarm?: boolean;
}

type T = (k: string, o?: Record<string, unknown>) => string;

/** 合併列的整波（自己＋also）。 */
const waveIds = (it: WbItem) => [it.doc.event_id, ...it.also];

/** 一列的接手時限：合併列看整波裡還在計時、最早到期的那一則（SPEC「合併列看最早到期的那一則」）；都停了用列首的。 */
function rowSla(it: WbItem, alarms: ReturnType<typeof useAlarmStates>): AlarmSla | undefined {
  let best: AlarmSla | undefined;
  for (const id of waveIds(it)) {
    const s = alarms.slaOf(id);
    if (!s || !slaRunning(alarms.infoOf(id)?.state)) continue;
    if (!best || Date.parse(s.due_at) < Date.parse(best.due_at)) best = s;
  }
  return best ?? alarms.slaOf(it.doc.event_id);
}

export function AlarmWorkbench({
  items,
  mode,
  tree,
  tzOf,
  footer,
}: {
  items: WbItem[];
  mode: 'split' | 'drawer';
  tree: VmsDevice[];
  tzOf: (homeId: string) => string;
  /** 清單最後（〔載入更多〕/「已載入全部」） */
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const { session } = useApp();
  const me = session?.user?.user_id ?? '';
  const alarms = useAlarmStates();
  const now = useNow(15_000);
  const [sp, setSp] = useSearchParams();
  const [filter, setFilter] = useState<WbFilter>('all');
  const listRef = useRef<HTMLUListElement>(null);

  const facts = useCallback(
    (it: WbItem): WbRowFacts => {
      const info = alarms.infoOf(it.doc.event_id);
      return { state: info?.state, info, sla: rowSla(it, alarms), recovered: recoveredAt(it.doc.key, it.ep) !== null, listed: it.listed };
    },
    [alarms],
  );
  const shown = useMemo(() => items.filter((it) => passFilter(filter, facts(it), me, now)), [items, filter, facts, me, now]);
  // 篩選膠囊的數字：同一份還沒處理的清單（跟選單徽章同一個來源；同一次的開始＋結束算一次）
  const counts = useMemo(() => {
    let mine = 0;
    let over = 0;
    for (const a of alarms.openList) {
      const info = alarms.infoOf(a.event_id) ?? a.alarm;
      const f: WbRowFacts = { state: info?.state, info, sla: a.sla, recovered: recoveredAt(a.key, a.ep) !== null, listed: alarms.listed(a.home_id, a.device_ref) };
      if (isMine(f, me)) mine++;
      if (isOverdue(f, now)) over++;
    }
    return { all: alarms.unhandled, mine, overdue: over };
  }, [alarms, me, now]);

  // 選取（網址 sel，重新整理 / 上一頁 / 通知連結回到同一則；沒有 = 第一列）
  // 網址的 sel 指到已合併進某一列的那則（通知連結、同一台＋同一種一直併）→ 選那一列
  const rawSel = sp.get('sel');
  const urlSel = useMemo(() => {
    if (!rawSel || shown.some((x) => x.id === rawSel)) return rawSel;
    const hit = shown.find((x) => x.also.some((e) => `${x.doc.home_id}/${e}` === rawSel));
    return hit ? hit.id : rawSel;
  }, [rawSel, shown]);
  const ids = shown.map((x) => x.id);
  const prevIds = useRef<string[]>(ids);
  const [done, setDone] = useState(false);
  const selId = done ? null : urlSel && ids.includes(urlSel) ? urlSel : (keepSelection(prevIds.current, ids, urlSel) ?? null);
  useEffect(() => {
    prevIds.current = ids;
  });
  const select = useCallback(
    (id: string | null) => {
      setDone(id === null);
      setSp(
        (cur) => {
          const n = new URLSearchParams(cur);
          if (id) n.set('sel', id);
          else n.delete('sel');
          return n;
        },
        { replace: true },
      );
    },
    [setSp],
  );
  const sel = shown.find((x) => x.id === selId) ?? null;
  // 選取跟著事件走：網址還沒有 sel（剛打開）或 sel 那則不見了 → 把現在選到的寫進網址，之後排序變了也還是那一則
  useEffect(() => {
    if (selId && selId !== rawSel) select(selId);
  }, [selId, rawSel, select]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const paneOpen = mode === 'split' || drawerOpen;
  // 選取那列捲到看得見
  useEffect(() => {
    if (!selId) return;
    document.getElementById(optionId(selId))?.scrollIntoView?.({ block: 'nearest' });
  }, [selId]);

  // 右欄的狀態：提示、409、閃一下的按鈕、對話框
  const [msg, setMsg] = useState<{ kind: 'warn' | 'error' | 'info'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [noteFocus, setNoteFocus] = useState(0);
  const [enlarge, setEnlarge] = useState(false);
  const [help, setHelp] = useState(false);
  const [waveOpen, setWaveOpen] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => {
    setMsg(null);
    setNote('');
    setAssignOpen(false);
    setWaveOpen(false);
  }, [selId]);
  useEffect(() => {
    if (!msg || msg.kind === 'error') return;
    const id = window.setTimeout(() => setMsg(null), msg.kind === 'info' ? 3000 : 8000);
    return () => window.clearTimeout(id);
  }, [msg]);
  const focusList = () => listRef.current?.focus({ preventScroll: true });
  const blink = (k: string) => {
    setFlash(k);
    window.setTimeout(() => setFlash((f) => (f === k ? null : f)), 300);
  };

  /** 狀態變更（鍵盤與按鈕共用）：畫面上這筆沒有版本就先讀；409 → 重畫、提示是誰改的，選取不動；合併列整波一起（被別人改掉的跳過）。 */
  const changeState = async (it: WbItem, to: AlarmStateName): Promise<'ok' | 'conflict' | 'error' | 'skip'> => {
    const homeId = it.doc.home_id;
    const eventId = it.doc.event_id;
    const api = alarms.apiOf(homeId);
    if (!api || busy) return 'skip';
    if (!alarms.canHandle(homeId)) {
      setMsg({ kind: 'info', text: t('alarm.read_only') });
      return 'skip';
    }
    const info: AlarmInfo = alarms.infoOf(eventId) ?? { state: 'open' };
    if (!workbenchActions(info, me).includes(to)) {
      setMsg({ kind: 'info', text: t('wb.cannot', { state: t(`alarm.state_${info.state}`), act: t(`alarm.act_${to}`) }) });
      return 'skip';
    }
    if (!noteOK(note)) {
      setMsg({ kind: 'error', text: t('alarm.note_invalid', { max: ALARM_NOTE_MAX }) });
      return 'skip';
    }
    setBusy(true);
    setMsg(null);
    try {
      let cur = info;
      if (cur.version === undefined) {
        cur = (await alarmApi.get(api, homeId, eventId)).alarm;
        if (cur.state !== info.state) {
          alarms.set(homeId, eventId, cur);
          setMsg({ kind: 'warn', text: t('alarm.conflict_anon') });
          return 'conflict';
        }
      }
      const r = await alarmApi.setState(api, homeId, eventId, { state: to, expected_state: cur.state, expected_version: cur.version ?? -1, note: note.trim() || undefined });
      alarms.set(homeId, eventId, r.alarm);
      let skipped = 0;
      for (const id of it.also) {
        try {
          let o = alarms.infoOf(id);
          if (!o || o.version === undefined) o = (await alarmApi.get(api, homeId, id)).alarm;
          // 這則照轉換規則能改成目標狀態才套用（例：被指派過、已看到的照樣能接手）；已結案 / 已是目標狀態的跳過
          if (!workbenchActions(o, me).includes(to) || (o.state === to && to !== 'claimed')) {
            skipped++;
            continue;
          }
          const rr = await alarmApi.setState(api, homeId, id, { state: to, expected_state: o.state, expected_version: o.version ?? -1, note: note.trim() || undefined });
          alarms.set(homeId, id, rr.alarm);
        } catch (e) {
          const c = conflictOf(e);
          if (c) alarms.set(homeId, id, c.current);
          skipped++;
        }
      }
      setNote('');
      if (skipped) setMsg({ kind: 'warn', text: t('wb.wave_skipped', { n: skipped }) });
      else if (it.also.length) setMsg({ kind: 'info', text: t('alarm.also_done', { n: it.also.length + 1 }) });
      return 'ok';
    } catch (e) {
      const c = conflictOf(e);
      if (c) {
        alarms.set(homeId, eventId, c.current);
        setMsg({ kind: 'warn', text: t('wb.conflict', { name: c.byName || t('alarm.someone'), state: t(`alarm.state_${c.current.state}`) }) });
        return 'conflict';
      }
      setMsg({ kind: 'error', text: t('alarm.failed', { msg: e instanceof Error ? e.message : String(e) }) });
      return 'error';
    } finally {
      setBusy(false);
    }
  };

  /** 處理完自動到下一則：R / F（與〔已恢復，結案〕）成功後，原本排在下面、還沒處理的第一則。 */
  const actOn = async (it: WbItem, to: AlarmStateName) => {
    blink(to);
    const before = shown.map((x) => ({ id: x.id, unhandled: x.listed && handling(alarms.infoOf(x.doc.event_id)?.state) === 'unhandled' }));
    const res = await changeState(it, to);
    focusList();
    if (res === 'ok' && (to === 'resolved' || to === 'false_alarm')) select(nextUnhandled(before, it.id));
  };

  const assign = async (it: WbItem, userId: string | null) => {
    const homeId = it.doc.home_id;
    const api = alarms.apiOf(homeId);
    setAssignOpen(false);
    if (!api) return;
    setBusy(true);
    setMsg(null);
    let skipped = 0;
    let first = true;
    try {
      for (const id of waveIds(it)) {
        try {
          let o = alarms.infoOf(id);
          if (!o || o.version === undefined) o = (await alarmApi.get(api, homeId, id)).alarm;
          if (!first && (handling(o.state) === 'closed' || o.assigned_to === (userId ?? undefined))) continue;
          const r = await alarmApi.assign(api, homeId, id, { assignee: userId, expected_version: o.version ?? -1, note: first ? note.trim() || undefined : undefined });
          alarms.set(homeId, id, r.alarm);
        } catch (e) {
          const c = conflictOf(e);
          if (c) {
            alarms.set(homeId, id, c.current);
            if (first) {
              setMsg({ kind: 'warn', text: t('wb.conflict', { name: c.byName || t('alarm.someone'), state: t(`alarm.state_${c.current.state}`) }) });
              return;
            }
          } else if (first) {
            setMsg({ kind: 'error', text: assignErrText(t, e) });
            return;
          }
          skipped++;
        } finally {
          first = false;
        }
      }
      setNote('');
      if (skipped) setMsg({ kind: 'warn', text: t('wb.wave_skipped', { n: skipped }) });
    } finally {
      setBusy(false);
      focusList();
    }
  };

  const saveNote = async (it: WbItem) => {
    const homeId = it.doc.home_id;
    const api = alarms.apiOf(homeId);
    const text = note.trim();
    if (!api || !text) return focusList();
    if (!noteOK(text)) return setMsg({ kind: 'error', text: t('alarm.note_invalid', { max: ALARM_NOTE_MAX }) });
    setBusy(true);
    try {
      let o = alarms.infoOf(it.doc.event_id);
      if (!o || o.version === undefined) o = (await alarmApi.get(api, homeId, it.doc.event_id)).alarm;
      const r = await alarmApi.note(api, homeId, it.doc.event_id, { note: text, expected_version: o.version ?? -1 });
      alarms.set(homeId, it.doc.event_id, r.alarm);
      setNote('');
      setMsg({ kind: 'info', text: t('wb.note_saved') });
    } catch (e) {
      const c = conflictOf(e);
      if (c) {
        alarms.set(homeId, it.doc.event_id, c.current);
        setMsg({ kind: 'warn', text: t('wb.conflict', { name: c.byName || t('alarm.someone'), state: t(`alarm.state_${c.current.state}`) }) });
      } else setMsg({ kind: 'error', text: t('alarm.failed', { msg: e instanceof Error ? e.message : String(e) }) });
    } finally {
      setBusy(false);
      focusList();
    }
  };

  // 鍵盤（同 #vms-web-973 的不攔截條件；抽屜不是對話框，開著照樣能用）
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => undefined);
  keyRef.current = (e) => {
    if (e.isComposing) return;
    const el = e.target as HTMLElement | null;
    if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
    if (document.querySelector('dialog[open], [role="dialog"]')) return;
    const k: WbKey | null = wbKeyOf(e);
    if (!k) {
      if (e.key === 'Escape' && mode === 'drawer' && drawerOpen) {
        setDrawerOpen(false);
        focusList();
      }
      return;
    }
    e.preventDefault();
    if (k === 'help') return setHelp(true);
    if (k === 'next' || k === 'prev') {
      if (!shown.length) return;
      const i = selId ? ids.indexOf(selId) : -1;
      const n = Math.max(0, Math.min(ids.length - 1, i + (k === 'next' ? 1 : -1)));
      select(ids[n]);
      focusList();
      return;
    }
    if (!sel) return;
    if (k === 'enlarge') {
      if (mode === 'drawer' && !drawerOpen) setDrawerOpen(true);
      else setEnlarge(true);
      return;
    }
    if (k === 'wave') return setWaveOpen((o) => !o);
    if (mode === 'drawer' && !drawerOpen) setDrawerOpen(true);
    const to = KEY_STATE[k];
    if (to) return void actOn(sel, to);
    if (!alarms.canHandle(sel.doc.home_id)) return setMsg({ kind: 'info', text: t('alarm.read_only') });
    if (k === 'assign') {
      if (handling(alarms.infoOf(sel.doc.event_id)?.state) === 'closed') return setMsg({ kind: 'info', text: t('wb.assign_closed') });
      return setAssignOpen(true);
    }
    if (k === 'note') setNoteFocus((x) => x + 1);
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyRef.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // 兩欄的高度：視窗底部為止（兩欄各自捲動）
  const rootRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  useEffect(() => {
    const fit = () => {
      const top = rootRef.current?.getBoundingClientRect().top ?? 0;
      setHeight(Math.max(320, window.innerHeight - top - 12));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  const pane = sel ? (
    <WorkbenchPane
      key={sel.id}
      it={sel}
      tree={tree}
      tz={tzOf(sel.doc.home_id)}
      now={now}
      me={me}
      busy={busy}
      flash={flash}
      msg={msg}
      note={note}
      setNote={setNote}
      noteFocus={noteFocus}
      waveOpen={waveOpen}
      onWave={() => setWaveOpen((o) => !o)}
      onAct={(to) => void actOn(sel, to)}
      onAssign={() => setAssignOpen(true)}
      onSaveNote={() => void saveNote(sel)}
      onNoteEscape={() => {
        setNote('');
        focusList();
      }}
      onEnlarge={() => setEnlarge(true)}
      assignMenu={
        assignOpen ? (
          <AssignMenu
            it={sel}
            current={alarms.infoOf(sel.doc.event_id)?.assigned_to}
            onPick={(u) => void assign(sel, u)}
            onClose={() => {
              setAssignOpen(false);
              focusList();
            }}
          />
        ) : null
      }
    />
  ) : (
    <DoneView items={items} />
  );

  return (
    <div className={`wb wb-${mode}`} ref={rootRef} style={{ height }} data-testid="alarm-workbench" data-mode={mode}>
      <div className="wb-left">
        <div className="wb-filters" role="group" aria-label={t('wb.filter')}>
          {WB_FILTERS.map((f) => (
            <button key={f} type="button" className={`chip${filter === f ? ' on' : ''}`} aria-pressed={filter === f} data-wb-filter={f} onClick={() => setFilter(f)}>
              {t(`wb.filter_${f}`)} <span className="muted">{f === 'all' ? (counts.all ?? '…') : counts[f]}</span>
            </button>
          ))}
          <span className="spacer" />
          <button type="button" className="btn btn-sm" onClick={() => setHelp(true)} title={t('wb.help_title')} data-testid="wb-help-btn">
            ?
          </button>
        </div>
        <ul
          className="event-list wb-list"
          role="listbox"
          tabIndex={0}
          ref={listRef}
          aria-label={t('wb.list')}
          aria-activedescendant={selId ? optionId(selId) : undefined}
          data-testid="wb-list"
        >
          {shown.map((it) => (
            <WbRow
              key={it.id}
              it={it}
              selected={it.id === selId}
              now={now}
              me={me}
              onClick={() => {
                select(it.id);
                if (mode === 'drawer') setDrawerOpen(true);
                focusList();
              }}
            />
          ))}
          {shown.length === 0 && <li className="muted wb-empty">{t(filter === 'all' ? 'wb.empty' : `wb.empty_${filter}`)}</li>}
        </ul>
        {filter === 'all' && footer && <div className="wb-footer">{footer}</div>}
      </div>
      {mode === 'split' && <section className="wb-pane" aria-label={t('wb.pane')} data-testid="wb-pane">{pane}</section>}
      {mode === 'drawer' && paneOpen && (
        <section className="wb-pane wb-drawer" aria-label={t('wb.pane')} data-testid="wb-pane">
          <div className="row">
            <span className="spacer" />
            <button
              type="button"
              className="icon-btn"
              aria-label={t('common.close')}
              data-testid="wb-drawer-close"
              onClick={() => {
                setDrawerOpen(false);
                focusList();
              }}
            >
              ×
            </button>
          </div>
          {pane}
        </section>
      )}
      <Dialog open={help} title={t('wb.help_title')} onClose={() => setHelp(false)} testId="wb-help">
        <table className="grid small">
          <tbody>
            {(['jk', 'a', 'c', 'r', 'f', 's', 'n', 'enter', 'x', 'q', 'esc'] as const).map((k) => (
              <tr key={k}>
                <th scope="row" className="mono">
                  {t(`wb.key_${k}`)}
                </th>
                <td>{t(`wb.keydesc_${k}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted small">{t('wb.help_hint')}</p>
      </Dialog>
      {sel && <EnlargeDialog open={enlarge} it={sel} tree={tree} onClose={() => (setEnlarge(false), focusList())} />}
    </div>
  );
}

const optionId = (id: string) => 'wb-opt-' + id.replace(/[^A-Za-z0-9_-]/g, '_');

function assignErrText(t: T, e: unknown): string {
  const code = (e as { code?: string }).code;
  if (code === 'ASSIGNEE_NOT_ELIGIBLE') return t('wb.assign_not_eligible');
  if (code === 'INVALID_TRANSITION') return t('wb.assign_invalid');
  return t('alarm.failed', { msg: e instanceof Error ? e.message : String(e) });
}

/** 左欄一列（一行 40 px）：色條、圖示、事件名、設備名、× N、時限、指派、狀態、已開多久。 */
function WbRow({ it, selected, now, me, onClick }: { it: WbItem; selected: boolean; now: number; me: string; onClick(): void }) {
  const { t } = useTranslation();
  const alarms = useAlarmStates();
  const info = alarms.infoOf(it.doc.event_id);
  const st = info?.state;
  const h = handling(st);
  const recovered = recoveredAt(it.doc.key, it.ep) !== null;
  const group = !it.listed ? 'unlisted' : h === 'closed' ? 'closed' : h === 'claimed' ? 'claimed' : recovered ? 'recovered' : it.doc.key === 'kinio_video_signal.video_loss' ? 'status' : 'security';
  const sla = it.listed ? slaView(rowSla(it, alarms), st, recovered, now) : null;
  const slaText = slaChipText(t, sla);
  const assignee = info?.assigned_to ? (info.assigned_to === me ? t('wb.me') : info.assigned_to_name || t('wb.left_member')) : '';
  const stateText =
    h === 'claimed' ? (info?.claimed_by === me ? t('alarm.claimed_by_me') : t('alarm.claimed_by', { name: info?.claimed_by_name || t('alarm.someone') })) : h === 'closed' ? t(`alarm.state_${st}`) : '';
  const label = [t(`wb.group_${group}`), it.name, it.devLabel, it.times, assignee && t('wb.assigned_to', { name: assignee }), slaText, stateText, it.age].filter(Boolean).join(' · ');
  return (
    <li
      id={optionId(it.id)}
      role="option"
      aria-selected={selected}
      aria-label={label}
      className={`event-row wb-row wb-g-${group}${selected ? ' selected' : ''}${it.unseen ? ' unseen' : ''}`}
      data-event={it.doc.event_id}
      data-wb-group={group}
      onClick={onClick}
    >
      <span className="wb-name">{it.name}</span>
      <span className="wb-dev muted">{it.devLabel}</span>
      {recovered && (
        <span className="alarm-recovered small" data-testid="alarm-recovered">
          {t('wb.recovered')}
        </span>
      )}
      {it.times && (
        <span className="chip alarm-times" data-testid="alarm-group">
          {it.times}
        </span>
      )}
      {slaText && (
        <span className={`chip wb-sla wb-sla-${sla?.kind === 'over' ? 'over' : sla?.kind === 'running' ? sla.tone : ''}`} data-testid="wb-sla">
          {slaText}
        </span>
      )}
      {assignee && (
        <span className={`wb-assignee${info?.assigned_to === me ? ' me' : ''}`} title={info?.assigned_to_name} data-testid="wb-assignee">
          → {assignee}
        </span>
      )}
      {stateText && <span className="small muted wb-state">{stateText}</span>}
      <span className="muted small alarm-age wb-age" data-testid="alarm-age">
        {it.age}
      </span>
    </li>
  );
}

interface PaneProps {
  it: WbItem;
  tree: VmsDevice[];
  tz: string;
  now: number;
  me: string;
  busy: boolean;
  flash: string | null;
  msg: { kind: 'warn' | 'error' | 'info'; text: string } | null;
  note: string;
  setNote(v: string): void;
  noteFocus: number;
  waveOpen: boolean;
  onWave(): void;
  onAct(to: AlarmStateName): void;
  onAssign(): void;
  onSaveNote(): void;
  onNoteEscape(): void;
  onEnlarge(): void;
  assignMenu: ReactNode;
}

/** 右欄：標題列、處理列、影像、關聯攝影機、平面圖、這一波、歷程。 */
function WorkbenchPane(p: PaneProps) {
  const { t, i18n } = useTranslation();
  const { it, me, now } = p;
  const alarms = useAlarmStates();
  const homeId = it.doc.home_id;
  const eventId = it.doc.event_id;
  const api = alarms.apiOf(homeId);
  const info = alarms.infoOf(eventId);
  const can = alarms.canHandle(homeId);
  const detail = useQuery({
    queryKey: ['alarm', api, homeId, eventId, info?.version ?? -1, info?.updated_at ?? ''],
    enabled: !!api,
    retry: false,
    queryFn: () => alarmApi.get(api!, homeId, eventId),
  });
  const cur: AlarmInfo = info ?? detail.data?.alarm ?? { state: 'open' };
  const sla: AlarmSla | undefined = rowSla(it, alarms) ?? detail.data?.sla;
  const recovered = recoveredAt(it.doc.key, it.ep) !== null;
  const view = it.listed ? slaView(sla, cur.state, recovered, now) : null;
  const history = detail.data?.history ?? [];
  const acts = can ? workbenchActions(cur, me) : [];
  const noteRef = useRef<HTMLInputElement>(null);
  // 只在這次按了 N 才把焦點放進備註框（換一則時右欄重新掛上，不搶清單的焦點）
  const noteSeen = useRef(p.noteFocus);
  useEffect(() => {
    if (p.noteFocus !== noteSeen.current) noteRef.current?.focus();
    noteSeen.current = p.noteFocus;
  }, [p.noteFocus]);
  const [live, setLive] = useState(false);
  const ts = it.ts / 1000;
  const dev = it.device;
  const all = useMemo(() => p.tree.flatMap((d) => [d, ...d.children]), [p.tree]);
  const parent = dev?.parentAid ? all.find((d) => d.homeId === dev.homeId && d.aid === dev.parentAid) : undefined;
  return (
    <div className="wb-pane-body" data-wb-sel={eventId}>
      <header className="wb-head">
        <h2 className="wb-title">{it.name}</h2>
        <div className="small">
          {dev ? <Link to={deviceHref(dev)}>{it.devLabel}</Link> : it.devLabel}
          <span className="muted">
            {' '}
            · {it.homeName} · {formatDateTime(ts, p.tz, i18n.language, true)}
          </span>
        </div>
        <div className="row wb-head-badges">
          <AlarmStatus info={cur} />
          {it.ep && <EpisodeNote ep={it.ep} eventKey={it.doc.key} tz={p.tz} />}
          {it.times && <span className="chip alarm-times">{it.times}</span>}
          {view && <SlaLine sla={sla!} view={view} history={history} />}
          {cur.assigned_to && (
            <span className="small" data-testid="wb-assigned">
              {t(cur.assigned_at ? 'wb.assigned_line' : 'wb.assigned_line_noat', {
                name: cur.assigned_to === me ? t('wb.me') : cur.assigned_to_name || t('wb.left_member'),
                by: cur.assigned_by === me ? t('wb.me') : cur.assigned_by_name || t('wb.left_member'),
                at: cur.assigned_at ? stamp(cur.assigned_at, { tz: p.tz }) : '',
              })}
            </span>
          )}
        </div>
        {it.startedBeforeAlarm && (
          <p className="muted small" data-testid="wb-before-armed">
            {t('wb.started_before_alarm')}
          </p>
        )}
      </header>

      <div className="wb-actions" data-testid="wb-actions">
        {can && recovered && isUnhandled(cur.state) && (
          <button type="button" className={`btn btn-sm${p.flash === 'resolved' ? ' flash' : ''}`} disabled={p.busy} data-wb-action="resolve-recovered" onClick={() => p.onAct('resolved')}>
            {t('alarm.act_resolve_recovered')}
          </button>
        )}
        {acts.map((to) => (
          <button
            key={to}
            type="button"
            className={`btn btn-sm${to === 'false_alarm' || to === 'open' ? '' : ' btn-primary'}${p.flash === to ? ' flash' : ''}`}
            disabled={p.busy || !api}
            data-wb-action={to}
            onClick={() => p.onAct(to)}
          >
            {to === 'claimed' && cur.state === 'claimed' ? t('alarm.act_take_over') : t(`alarm.act_${to}`)}
            {STATE_KEY[to] && <kbd className="wb-kbd">{STATE_KEY[to]}</kbd>}
          </button>
        ))}
        {can && handling(cur.state) !== 'closed' && (
          <span className="ot-menu-wrap">
            <button type="button" className="btn btn-sm" disabled={p.busy || !api} data-wb-action="assign" aria-haspopup="listbox" onClick={p.onAssign}>
              {t('wb.assign')}
              <kbd className="wb-kbd">S</kbd>
            </button>
            {p.assignMenu}
          </span>
        )}
        {can && (
          <span className="wb-note">
            <input
              ref={noteRef}
              className="input input-sm alarm-note"
              data-wb-note
              value={p.note}
              maxLength={ALARM_NOTE_MAX}
              placeholder={t('wb.note_placeholder', { max: ALARM_NOTE_MAX })}
              aria-label={t('alarm.note_label')}
              onChange={(e) => p.setNote(e.target.value.replace(/[\r\n]/g, ' '))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  p.onSaveNote();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  p.onNoteEscape();
                }
              }}
            />
            <button type="button" className="btn btn-sm" disabled={p.busy || !p.note.trim()} data-wb-action="note" onClick={p.onSaveNote}>
              {t('alarm.note_add')}
              <kbd className="wb-kbd">N</kbd>
            </button>
          </span>
        )}
        {!can && <p className="muted small">{t('wb.read_only')}</p>}
      </div>
      <div className="wb-msg small" role="status" aria-live="polite" data-testid="wb-msg" data-kind={p.msg?.kind}>
        {p.msg?.text}
      </div>

      <div className="wb-media">
        {dev?.isCamera ? (
          <ClipPlayer dev={dev} ts={ts} eventId={eventId} lens={lensOf(it.doc.payload) ?? 0} parent={parent} />
        ) : (
          <div className="card muted small">{t('wb.no_media')}</div>
        )}
        {dev?.isCamera && (
          <div className="card wb-live">
            <h3>{t('wb.live')}</h3>
            {live ? (
              <LiveTile cam={dev} label={it.devLabel} stream="sub" onClose={() => setLive(false)} />
            ) : (
              <button type="button" className="btn btn-sm" onClick={() => setLive(true)} data-testid="wb-live-btn" disabled={dev.presence === 'offline'}>
                {dev.presence === 'offline' ? t('wb.live_offline') : t('wb.live_open')}
              </button>
            )}
          </div>
        )}
      </div>
      <RelatedSnaps it={it} all={all} tree={p.tree} />
      <FloorplanMini it={it} />
      {it.n > 1 && <WaveList it={it} open={p.waveOpen} onToggle={p.onWave} tz={p.tz} now={now} />}
      <section className="card wb-history">
        <h3>{t('alarm.history')}</h3>
        {history.length === 0 ? <p className="muted small">{t('alarm.history_empty')}</p> : <AlarmHistoryList history={history} tz={p.tz} me={me} />}
      </section>
      {it.listed ? null : <p className="muted small">{t('wb.unlisted')}</p>}
      <p className="muted small">
        <button type="button" className="btn btn-sm" onClick={p.onEnlarge} data-testid="wb-enlarge">
          {t('wb.enlarge')} <kbd className="wb-kbd">Enter</kbd>
        </button>{' '}
        <Link className="btn btn-sm" to={`/events/${encodeURIComponent(homeId)}/${encodeURIComponent(eventId)}`} state={{ doc: it.doc }}>
          {t('evd.open')}
        </Link>
      </p>
    </div>
  );
}

/** 時限說明：剩 / 逾時 / 已恢復不計時 / 幾分鐘內接手；滑過寫來源。 */
function SlaLine({ sla, view, history }: { sla: AlarmSla; view: NonNullable<ReturnType<typeof slaView>>; history: AlarmChange[] }) {
  const { t } = useTranslation();
  const { orgs } = useAlarmOrgNames();
  const src =
    sla.source.kind === 'share' ? t('wb.sla_src_share', { org: orgs.get(sla.source.org_id) ?? t('wb.sla_src_other_org') }) : sla.source.kind === 'site' ? t('wb.sla_src_site') : t('wb.sla_src_org');
  let text: string;
  if (view.kind === 'running' || view.kind === 'over') text = t('wb.sla_line', { m: sla.minutes, s: slaChipText(t, view) });
  else if (view.kind === 'recovered') text = t('wb.sla_recovered');
  else {
    const met = slaMetAt(history, sla.started_at);
    text = met ? slaMetText(t, sla, met) : t('wb.sla_stopped');
  }
  const tone = view.kind === 'over' ? 'over' : view.kind === 'running' ? view.tone : '';
  return (
    <span className={`chip wb-sla wb-sla-${tone}`} title={src} data-testid="wb-sla-line">
      {text}
    </span>
  );
}

/** 組織 id → 名稱（時限來源「監控中心（分享）的預設」；我不在那個組織 → 寫「另一個組織」）。 */
function useAlarmOrgNames(): { orgs: Map<string, string> } {
  const { orgs } = useScope();
  return { orgs: useMemo(() => new Map(orgs.map((o) => [o.org_id, o.display_name])), [orgs]) };
}

/** 指派選單：可打字篩選、↑ / ↓ 選、Enter 指派、Esc 關；最上面是「我」、有指派時最後一項〔取消指派〕。 */
function AssignMenu({ it, current, onPick, onClose }: { it: WbItem; current?: string; onPick(userId: string | null): void; onClose(): void }) {
  const { t } = useTranslation();
  const alarms = useAlarmStates();
  const api = alarms.apiOf(it.doc.home_id);
  const q = useQuery({
    queryKey: ['alarm-assignees', api, it.doc.home_id, it.doc.event_id],
    enabled: !!api,
    staleTime: 30_000,
    retry: false,
    queryFn: () => alarmApi.assignees(api!, it.doc.home_id, it.doc.event_id),
  });
  const [text, setText] = useState('');
  const [idx, setIdx] = useState(0);
  const people = (q.data?.assignees ?? []).filter((a) => !text.trim() || a.display_name.toLowerCase().includes(text.trim().toLowerCase()));
  const opts: { id: string | null; label: string }[] = [
    ...people.map((a) => ({ id: a.user_id, label: a.is_me ? t('wb.assign_me', { name: a.display_name }) : a.display_name || t('wb.left_member') })),
    ...(current ? [{ id: null, label: t('wb.unassign') }] : []),
  ];
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && onClose();
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);
  return (
    <div className="ot-menu wb-assign-menu" ref={ref} data-testid="wb-assign-menu">
      <input
        className="input input-sm"
        autoFocus
        value={text}
        placeholder={t('wb.assign_filter')}
        aria-label={t('wb.assign_filter')}
        aria-controls="wb-assign-list"
        onChange={(e) => {
          setText(e.target.value);
          setIdx(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setIdx((i) => Math.min(opts.length - 1, i + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setIdx((i) => Math.max(0, i - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const o = opts[idx];
            if (o && o.id !== current) onPick(o.id);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          }
        }}
      />
      {q.isLoading && <p className="muted small">{t('common.loading')}</p>}
      {q.isError && <p className="error small">{assignErrText(t, q.error)}</p>}
      <ul role="listbox" id="wb-assign-list" className="plain-list">
        {opts.map((o, i) => (
          <li
            key={o.id ?? '__none'}
            role="option"
            aria-selected={i === idx}
            aria-disabled={o.id === current}
            className={`wb-assign-opt${i === idx ? ' on' : ''}${o.id === current ? ' muted' : ''}`}
            data-assignee={o.id ?? ''}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => o.id !== current && onPick(o.id)}
          >
            {o.label}
            {o.id && o.id === current ? ` · ${t('wb.assigned_now')}` : ''}
          </li>
        ))}
        {q.isSuccess && opts.length === 0 && <li className="muted small">{t('wb.assign_none')}</li>}
      </ul>
      {q.data?.truncated && <p className="muted small">{t('wb.assign_truncated')}</p>}
    </div>
  );
}

/** 關聯攝影機同時刻：關聯的攝影機在事件時刻的快照（最多 4 格），點 = 回放那台那個時刻。 */
function RelatedSnaps({ it, all, tree }: { it: WbItem; all: VmsDevice[]; tree: VmsDevice[] }) {
  const { t } = useTranslation();
  const src = all.find((d) => d.homeId === it.doc.home_id && d.aid === it.doc.device_ref);
  const q = useQuery({
    queryKey: ['device-links', it.doc.home_id],
    queryFn: () => linkApi.list(src!.apiEndpoint, it.doc.home_id),
    enabled: !!src,
    staleTime: 60_000,
    retry: false,
  });
  const cams = (q.data?.data ?? [])
    .filter((l) => l.sensor_ref === it.doc.device_ref)
    .map((l) => all.find((d) => d.homeId === it.doc.home_id && d.aid === l.device_ref))
    .filter((d): d is VmsDevice => !!d && d.isCamera);
  if (!cams.length) return null;
  return (
    <section className="card wb-related" data-testid="wb-related">
      <h3>{t('evd.related')}</h3>
      <div className="wb-related-grid">
        {cams.slice(0, 4).map((c) => (
          <Link key={c.key} to={playbackHref(c.key, it.ts / 1000 - 5, { event: it.doc.event_id })} className="wb-related-cell" title={fullName(c, tree)}>
            <EventThumb api={c.apiEndpoint} homeId={c.homeId} device={c.aid} atMs={it.ts} alt={fullName(c, tree)} />
            <span className="small">{fullName(c, tree)}</span>
          </Link>
        ))}
      </div>
      {cams.length > 4 && <p className="muted small">{t('wb.related_more', { n: cams.length - 4 })}</p>}
    </section>
  );
}

/** 平面圖小圖：這台放在平面圖上時，顯示那張圖、圖釘放大閃一次；點 = 地圖頁。沒放就不畫。 */
function FloorplanMini({ it }: { it: WbItem }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const owners = useFloorplanOwners();
  const { list } = useFloorplans(owners);
  const hit = useMemo(() => {
    for (const fp of list) {
      const p = fp.placements.find((x) => x.home_id === it.doc.home_id && x.device_ref === it.doc.device_ref && x.x !== undefined);
      if (p) return { fp, p };
    }
    return null;
  }, [list, it.doc.home_id, it.doc.device_ref]);
  const img = useFloorplanImage(hit?.fp);
  if (!hit) return null;
  return (
    <section className="card wb-floorplan" data-testid="wb-floorplan">
      <h3>
        {t('wb.floorplan')} <span className="muted small">{hit.fp.name}</span>
      </h3>
      <button
        type="button"
        className="wb-fp"
        style={{ aspectRatio: `${hit.fp.width} / ${hit.fp.height}` }}
        onClick={() =>
          nav('/map', { state: { pins: [{ deviceKey: `${it.doc.home_id}/${it.doc.device_ref}`, ts: it.ts, eventId: it.doc.event_id, homeId: it.doc.home_id, type: it.doc.key, level: 'alarm' }] } })
        }
        aria-label={t('wb.floorplan_open')}
      >
        {img.data && <img src={img.data} alt="" />}
        <span className="wb-fp-pin" style={{ left: `${(hit.p.x ?? 0) * 100}%`, top: `${(hit.p.y ?? 0) * 100}%` }} />
      </button>
    </section>
  );
}

/** 這一波：同一台＋同一種還沒處理的每一次（時間、已開多久、狀態），最多 50 次；X 展開 / 收起（收起時列 5 次）。 */
function WaveList({ it, open, onToggle, tz, now }: { it: WbItem; open: boolean; onToggle(): void; tz?: string; now: number }) {
  const { t } = useTranslation();
  const alarms = useAlarmStates();
  const k = aggKey(it.doc.home_id, it.doc.device_ref, it.doc.key);
  const wave = alarms.openList.filter((a) => aggKey(a.home_id, a.device_ref, a.key) === k).sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at));
  const list = wave.length ? wave : [];
  const max = open ? 50 : 5;
  return (
    <section className="card wb-wave" data-testid="wb-wave">
      <h3>
        {t('wb.wave', { n: it.n })}{' '}
        <button type="button" className="btn btn-sm" aria-expanded={open} onClick={onToggle}>
          {open ? t('wb.wave_less') : t('wb.wave_more')} <kbd className="wb-kbd">X</kbd>
        </button>
      </h3>
      <ol className="plain-list small">
        {list.slice(0, max).map((a) => {
          const st = alarms.infoOf(a.event_id)?.state ?? a.alarm.state;
          return (
            <li key={a.event_id} data-wave-event={a.event_id}>
              {stamp(a.occurred_at, { tz })} · {t(`alarm.state_${st}`)} · <span className="muted">{ageShort(t, Date.parse(a.occurred_at), now)}</span>
            </li>
          );
        })}
      </ol>
      {list.length > max && <p className="muted small">{t('wb.wave_rest', { n: list.length - max })}</p>}
    </section>
  );
}

function ageShort(t: T, since: number, now: number): string {
  const m = Math.max(0, Math.floor((now - since) / 60_000));
  if (m < 1) return t('alarm.age_just_now');
  if (m < 60) return t('alarm.age_min', { count: m });
  const h = Math.floor(m / 60);
  return h < 24 ? t('alarm.age_hour', { count: h }) : t('alarm.age_day', { count: Math.floor(h / 24) });
}

/** 全部處理完了：最近結案的 5 則。 */
function DoneView({ items }: { items: WbItem[] }) {
  const { t } = useTranslation();
  const alarms = useAlarmStates();
  const closed = items.filter((x) => handling(alarms.infoOf(x.doc.event_id)?.state) === 'closed').slice(0, 5);
  return (
    <div className="wb-done" data-testid="wb-done">
      <p>{t(items.length ? 'wb.all_done' : 'wb.empty')}</p>
      {closed.length > 0 && (
        <>
          <h3 className="small">{t('wb.recent_closed')}</h3>
          <ul className="plain-list small">
            {closed.map((x) => (
              <li key={x.id}>
                {x.name} · {x.devLabel} · {t(`alarm.state_${alarms.infoOf(x.doc.event_id)?.state ?? 'resolved'}`)}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** 大圖：有片段 = 全螢幕播片段（這時才建回放 session）；沒有 = 事件快照。 */
function EnlargeDialog({ open, it, tree, onClose }: { open: boolean; it: WbItem; tree: VmsDevice[]; onClose(): void }) {
  const { t } = useTranslation();
  const dev = it.device;
  const all = tree.flatMap((d) => [d, ...d.children]);
  const parent = dev?.parentAid ? all.find((d) => d.homeId === dev.homeId && d.aid === dev.parentAid) : undefined;
  return (
    <Dialog open={open} title={`${it.name} · ${it.devLabel}`} onClose={onClose} wide testId="wb-enlarge-dialog">
      {dev?.isCamera && it.doc.has_clip ? (
        <ClipPlayer dev={dev} ts={it.ts / 1000} eventId={it.doc.event_id} lens={lensOf(it.doc.payload) ?? 0} parent={parent} autoStart />
      ) : dev?.isCamera ? (
        <EventThumb api={dev.apiEndpoint} homeId={dev.homeId} device={dev.aid} atMs={it.ts} alt={it.name} className="wb-big-thumb" lens={lensOf(it.doc.payload) ?? 0} />
      ) : (
        <p className="muted">{t('wb.no_media')}</p>
      )}
    </Dialog>
  );
}
